import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    Autocomplete,
    TextField,
    Button,
    IconButton,
    Stack,
    CircularProgress,
    Chip,
} from '@mui/material';
import { Close, CheckCircle } from '@mui/icons-material';
import { UserPlus } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany, ILeaderShip, IPosition } from '../../../redux/company/types';
import { searchUser } from '../../../redux/user/actions';
import { userSearchSelector, searchingUserSearchSelector } from '../../AvatarGroup/selector';
import { ThspianAvatar } from '../../Avatar/ThspianAvatar';
import _ from 'lodash';
import { createPosition, approvePosition, deletePosition } from '../../../redux/company/actions';
import { IUserSummary } from '../../../types/global/helper';
import { LEADERSHIP_POSITIONS } from '../../../data/forms/company.data';
import { COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface ICompanyLeadershipEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
    leadership?: ILeaderShip;
    otherPositions?: ILeaderShip;
}

const SURFACE_CARD = '#1a1a1a';
const SURFACE_HIGH = '#20201f';
const INPUT_BLACK = '#000000';

const blackInputSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: INPUT_BLACK,
        color: '#ffffff',
        borderRadius: 2,
        fontFamily: FONT_ENUM.MAIN_REGULAR,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&.Mui-focused fieldset': { borderColor: COMPANY_BRAND_ORANGE },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.35)', opacity: 1 },
};

const CompanyLeadershipEdit: React.FC<ICompanyLeadershipEdit> = ({
    company,
    isEditing,
    onSave,
    onChanges,
    leadership,
    otherPositions,
}) => {
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);

    const [activeTab, setActiveTab] = useState(0);
    const [newLeader, setNewLeader] = useState<{ title: string; user: IUserSummary | null }>({
        title: '',
        user: null,
    });

    const leadershipPositions = (leadership && leadership.data) || [];
    const otherPositionsData = (otherPositions && otherPositions.data) || [];

    useEffect(() => {
        onChanges(false);
    }, [onChanges]);

    const addLeader = () => {
        if (newLeader.title.trim() && newLeader.user && company?._id) {
            dispatch(
                createPosition({
                    company: company._id,
                    user: newLeader.user._id,
                    title: newLeader.title,
                }),
            );
            setNewLeader({ title: '', user: null });
        }
    };

    const handleApprovePosition = (positionId: string) => {
        dispatch(approvePosition({ positionId }));
    };

    const handleDeletePosition = (positionId: string) => {
        dispatch(deletePosition({ positionId }));
    };

    const handleSave = () => {};

    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave]);

    const handleUserSearch = useRef(
        _.debounce((searchTerm: string) => {
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm, withProfileSearch: true }));
            }
        }, 1000),
    ).current;

    const dropdownPaperSx = {
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '12px',
        maxHeight: 300,
    };

    const tablet1024RangeMq =
        '@media (min-width: 1000px) and (max-width: 1060px) and (min-height: 600px) and (max-height: 1366px)';

    const renderPositionCards = (positions: IPosition[], sectionKey: string) => {
        if (positions.length === 0) {
            return (
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.45)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontStyle: 'italic',
                        fontSize: 14,
                    }}>
                    No members added yet
                </Typography>
            );
        }
        return (
            <Grid
                container
                spacing={2}
                sx={{
                    [tablet1024RangeMq]: {
                        '& > .MuiGrid-item': {
                            flexBasis: '100%',
                            maxWidth: '100%',
                        },
                    },
                }}>
                {positions.map((position: IPosition, index: number) => (
                    <Grid item xs={12} sm={6} key={position._id}>
                        <Box
                            sx={{
                                position: 'relative',
                                bgcolor: SURFACE_CARD,
                                borderRadius: 3,
                                p: 2.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                border: '1px solid rgba(255,255,255,0.06)',
                                transition: 'background-color 0.15s ease',
                                '&:hover': { bgcolor: SURFACE_HIGH },
                                minHeight: 1,
                                [tablet1024RangeMq]: {
                                    p: 2,
                                    gap: 1.5,
                                },
                            }}>
                            <ThspianAvatar
                                user={position.user}
                                sx={{
                                    width: { xs: 56, [tablet1024RangeMq]: 48 },
                                    height: { xs: 56, [tablet1024RangeMq]: 48 },
                                    borderRadius: 2,
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                        color:
                                            sectionKey === 'leadership' && index === 0
                                                ? COMPANY_BRAND_ORANGE
                                                : 'rgba(255,255,255,0.45)',
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        mb: 0.25,
                                        [tablet1024RangeMq]: {
                                            fontSize: 10,
                                            letterSpacing: '0.04em',
                                        },
                                    }}>
                                    {position.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                        fontWeight: 700,
                                        color: '#ffffff',
                                        fontSize: '1rem',
                                        lineHeight: 1.2,
                                        [tablet1024RangeMq]: {
                                            fontSize: '0.95rem',
                                        },
                                    }}>
                                    {position.user.firstName} {position.user.lastName}
                                </Typography>
                                <Box sx={{ mt: 0.75 }}>
                                    <Chip
                                        label={position.approved ? 'Approved' : 'Pending Approval'}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            bgcolor: position.approved ? 'rgba(46, 204, 113, 0.2)' : 'rgba(255, 193, 7, 0.15)',
                                            color: position.approved ? '#2ecc71' : '#ffc107',
                                            [tablet1024RangeMq]: {
                                                height: 20,
                                                fontSize: '0.675rem',
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                {isEditing && !position.approved && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleApprovePosition(position._id)}
                                        sx={{
                                            color: '#2ecc71',
                                            '&:hover': { bgcolor: 'rgba(46, 204, 113, 0.12)' },
                                            [tablet1024RangeMq]: {
                                                p: 0.5,
                                            },
                                        }}>
                                        <CheckCircle fontSize="small" />
                                    </IconButton>
                                )}
                                {isEditing && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeletePosition(position._id)}
                                        sx={{
                                            color: 'rgba(255,255,255,0.45)',
                                            '&:hover': { color: COMPANY_BRAND_ORANGE, bgcolor: 'rgba(255,156,40,0.1)' },
                                            [tablet1024RangeMq]: {
                                                p: 0.5,
                                            },
                                        }}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderAddMemberPanel = () => (
        <Box
            sx={{
                bgcolor: SURFACE_HIGH,
                borderRadius: 3,
                p: 3,
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                maxWidth: { md: 560 },
            }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.BOLD_HEADER,
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    color: '#ffffff',
                    mb: 0.5,
                }}>
                Add New Member
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, mb: 3, fontFamily: FONT_ENUM.MEDIUM_TEXT }}>
                Assign a role to an existing platform user.
            </Typography>

            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.45)',
                    mb: 1,
                    px: 0.5,
                }}>
                Position
            </Typography>
            <Autocomplete
                options={LEADERSHIP_POSITIONS}
                freeSolo
                filterOptions={(options, { inputValue }) =>
                    options.filter(option => option.toLowerCase().includes(inputValue.toLowerCase()))
                }
                value={newLeader.title || ''}
                onChange={(_, newValue) => {
                    setNewLeader(prev => ({ ...prev, title: typeof newValue === 'string' ? newValue : '' }));
                }}
                onInputChange={(_, newInputValue) => {
                    setNewLeader(prev => ({ ...prev, title: newInputValue }));
                }}
                getOptionLabel={option => option}
                componentsProps={{
                    paper: { sx: dropdownPaperSx },
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder="e.g. Executive Producer"
                        fullWidth
                        sx={{ ...blackInputSx, mb: 2 }}
                        InputProps={{
                            ...params.InputProps,
                            sx: { pl: 1.5 },
                        }}
                    />
                )}
                renderOption={(props, option, { index }) => (
                    <li {...props} key={`${option}-${index}`}>
                        <Typography sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_MEDIUM, py: 0.5 }}>{option}</Typography>
                    </li>
                )}
            />

            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.45)',
                    mb: 1,
                    px: 0.5,
                }}>
                User search
            </Typography>
            <Autocomplete
                options={userResults}
                componentsProps={{
                    paper: { sx: dropdownPaperSx },
                }}
                getOptionLabel={option => {
                    if (typeof option === 'string') return option;
                    return `${option.firstName} ${option.lastName}`;
                }}
                value={newLeader.user}
                onChange={(_, newValue: IUserSummary | string | null) => {
                    setNewLeader(prev => ({
                        ...prev,
                        user: newValue && typeof newValue === 'object' && '_id' in newValue ? (newValue as IUserSummary) : null,
                    }));
                }}
                onInputChange={(_, newInputValue) => {
                    handleUserSearch(newInputValue);
                }}
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder="Search by name or email..."
                        fullWidth
                        sx={{ ...blackInputSx, mb: 2 }}
                        InputProps={{
                            ...params.InputProps,
                            sx: { pl: 1.5 },
                            endAdornment: (
                                <>
                                    {searchingUser ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                renderOption={(props, option, { index }) => {
                    if (typeof option === 'string') {
                        return null;
                    }
                    const u = option as IUserSummary;
                    return (
                        <li {...props} key={`${u._id}-${index}`}>
                            <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.5 }}>
                                <ThspianAvatar user={u} sx={{ width: 32, height: 32 }} />
                                <Typography sx={{ color: '#fff' }}>
                                    {u.firstName} {u.lastName}
                                </Typography>
                            </Stack>
                        </li>
                    );
                }}
            />

            <Stack spacing={1.5}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<UserPlus size={18} />}
                    onClick={addLeader}
                    disabled={!newLeader.title || !newLeader.user}
                    sx={{
                        borderRadius: 999,
                        py: 1.75,
                        borderColor: `${COMPANY_BRAND_ORANGE}55`,
                        color: COMPANY_BRAND_ORANGE,
                        fontWeight: 800,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        letterSpacing: '0.08em',
                        '&:hover': {
                            borderColor: COMPANY_BRAND_ORANGE,
                            bgcolor: 'rgba(255,156,40,0.08)',
                        },
                        '&.Mui-disabled': {
                            borderColor: 'rgba(255,255,255,0.12)',
                            color: 'rgba(255,255,255,0.3)',
                        },
                    }}>
                    ADD MEMBER
                </Button>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setNewLeader({ title: '', user: null })}
                    sx={{
                        borderRadius: 999,
                        color: '#ffffff',
                        borderColor: 'rgba(255,255,255,0.35)',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            borderColor: '#ffffff',
                            bgcolor: 'rgba(255,255,255,0.06)',
                        },
                    }}>
                    Cancel
                </Button>
            </Stack>
        </Box>
    );

    const tabBar = (
        <Box
            sx={{
                display: 'flex',
                gap: 4,
                borderBottom: '1px solid #262626',
                mb: 3,
            }}>
            <Box
                onClick={() => setActiveTab(0)}
                sx={{
                    pb: 2,
                    cursor: 'pointer',
                    borderBottom: activeTab === 0 ? `2px solid ${COMPANY_BRAND_ORANGE}` : '2px solid transparent',
                    mb: '-1px',
                    color: activeTab === 0 ? COMPANY_BRAND_ORANGE : 'rgba(255,255,255,0.45)',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    fontSize: 13,
                    fontFamily: FONT_ENUM.BOLD_HEADER,
                    transition: 'color 0.15s ease',
                }}>
                LEADERSHIP ({leadershipPositions.length})
            </Box>
            <Box
                onClick={() => setActiveTab(1)}
                sx={{
                    pb: 2,
                    cursor: 'pointer',
                    borderBottom: activeTab === 1 ? `2px solid ${COMPANY_BRAND_ORANGE}` : '2px solid transparent',
                    mb: '-1px',
                    color: activeTab === 1 ? COMPANY_BRAND_ORANGE : 'rgba(255,255,255,0.45)',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    fontSize: 13,
                    fontFamily: FONT_ENUM.BOLD_HEADER,
                    '&:hover': { color: 'rgba(255,255,255,0.75)' },
                }}>
                OTHER POSITIONS ({otherPositionsData.length})
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                        color: '#ffffff',
                        letterSpacing: '-0.02em',
                        mb: 1.5,
                    }}>
                    Team{' '}
                    <Box component="span" sx={{ color: COMPANY_BRAND_ORANGE }}>
                        Management
                    </Box>
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.55)',
                        maxWidth: 560,
                        lineHeight: 1.65,
                        fontSize: '0.95rem',
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    }}>
                    Add and approve leadership roles shown on your public company profile.
                </Typography>
            </Box>

            {isEditing ? (
                <Box>
                    {tabBar}
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255,255,255,0.45)',
                                    mb: 2,
                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                }}>
                                {activeTab === 0
                                    ? `Current Leadership (${leadershipPositions.length})`
                                    : `Other Positions (${otherPositionsData.length})`}
                            </Typography>
                            {activeTab === 0
                                ? renderPositionCards(leadershipPositions, 'leadership')
                                : renderPositionCards(otherPositionsData, 'other')}
                        </Grid>
                        {activeTab === 0 && (
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                {renderAddMemberPanel()}
                            </Grid>
                        )}
                    </Grid>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'rgba(255,255,255,0.45)',
                                mb: 2,
                            }}>
                            Current Leadership ({leadershipPositions.length})
                        </Typography>
                        {renderPositionCards(leadershipPositions, 'leadership')}
                    </Box>
                    {otherPositionsData.length > 0 && (
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255,255,255,0.45)',
                                    mb: 2,
                                }}>
                                Other Positions ({otherPositionsData.length})
                            </Typography>
                            {renderPositionCards(otherPositionsData, 'other')}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export { CompanyLeadershipEdit };
